"use client";

import { useEffect } from "react";
import posthog from "posthog-js";

/**
 * Global error boundary — the last-resort fallback for errors thrown in the
 * root or `[locale]` layout itself (those bubble above the in-locale
 * `[locale]/error.tsx`). It MUST render its own <html>/<body> because it
 * replaces the entire document when the layout shell fails. Per-page/route
 * errors are handled by the branded `[locale]/error.tsx`.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[GlobalError]", error);
    // Report to PostHog error tracking (captured even without analytics consent).
    posthog.captureException(error);
  }, [error]);

  return (
    <html lang="en">
      <body className="antialiased">
        <div className="flex min-h-screen flex-col items-center justify-center bg-surface px-21 py-55 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-gold-ink">
            Something went wrong
          </p>
          <h1 className="mt-13 font-serif text-4xl uppercase leading-tight text-navy">
            Unexpected Error
          </h1>
          <p className="mx-auto mt-13 max-w-sm text-sm leading-relaxed text-gray-600">
            We ran into a problem loading this page. This has been noted. Please
            try again or return home.
          </p>
          {error.digest && (
            <p className="mt-8 font-mono text-xs text-gray-500">
              Error ID: {error.digest}
            </p>
          )}
          <div className="mt-34 flex flex-wrap justify-center gap-13">
            <button
              onClick={reset}
              className="rounded-control bg-gold px-21 py-13 text-sm font-semibold uppercase tracking-[0.04em] text-navy transition-colors hover:bg-gold-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
            >
              Try again
            </button>
            {/* A global error boundary replaces the whole document and may run
                with a broken router context, so a hard <a> navigation is the
                robust choice here — not next/link. */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a
              href="/"
              className="rounded-control border border-navy/20 px-21 py-13 text-sm font-semibold uppercase tracking-[0.04em] text-navy transition-colors hover:border-navy/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
            >
              Back to home
            </a>
          </div>
        </div>
      </body>
    </html>
  );
}
